/**
 * The small amount of DOM building the tools need.
 *
 * Deliberately not a rendering library. The site ships no client-side
 * framework, these pages are one input and a handful of output panels, and the
 * whole of what was missing from `document.createElement` is somewhere to put
 * the attributes and the children.
 */

/** What an element is built from: its attributes and what goes inside it. */
export interface ElementSpec {
  readonly className?: string;
  readonly text?: string;
  /** Anything else, as attributes. `undefined` values are left off. */
  readonly attributes?: Readonly<Record<string, string | undefined>>;
  readonly children?: readonly Node[];
}

/**
 * Build an element.
 *
 * Text goes on as `textContent` rather than as markup, which is the point of
 * building the output this way: everything the tools display came from
 * something the visitor typed, and there is no path here that could interpret
 * it as HTML.
 */
export function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  spec: ElementSpec = {},
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);

  if (spec.className !== undefined) {
    created.className = spec.className;
  }

  if (spec.text !== undefined) {
    created.textContent = spec.text;
  }

  const attributes = Object.entries(spec.attributes ?? {});

  for (const [name, value] of attributes) {
    if (value !== undefined) {
      created.setAttribute(name, value);
    }
  }

  created.append(...(spec.children ?? []));

  return created;
}

/**
 * Replace everything inside an element.
 */
export function replaceChildren(
  target: Element,
  children: readonly Node[],
): void {
  target.replaceChildren(...children);
}

/**
 * The element a container holds under a given `data-cb` name, if it has one.
 *
 * The templates mark their slots with `data-cb="output"` and the like, so that
 * a layout change that renames a class does not silently detach the script
 * from the page it drives.
 */
export function slot(
  container: Element,
  name: string,
): HTMLElement | undefined {
  return (
    container.querySelector<HTMLElement>(`[data-cb="${CSS.escape(name)}"]`) ??
    undefined
  );
}
